import * as Form from '@radix-ui/react-form';
import type { CSSProperties, ReactNode, ChangeEvent, FocusEvent } from 'react';
import { useState, forwardRef, useEffect, useRef } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCalendar, faClock, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { ptBR } from 'date-fns/locale';
import { format, isValid, parse, set } from 'date-fns';

import 'react-datepicker/dist/react-datepicker.css';
import styles from './calendar-input.module.scss';
import useScreenType from '@/hooks/util/useScreenType';
import { colors } from '@/Themes/Colors';

registerLocale('pt-BR', ptBR);

/**
 * Modo de seleção do {@link CalendarInput}.
 *
 * - `date` — somente data (ícone `faCalendar`)
 * - `time` — somente horário (ícone `faClock`)
 * - `datetime` — data e horário (ícone `faCalendar`)
 */
export type CalendarInputMode = 'date' | 'time' | 'datetime';

export interface CalendarInputProps {
  /** Nome do campo (obrigatório). Usado no Form.Field e no input hidden. */
  name: string;
  /** Label exibido acima do input. */
  label?: ReactNode;
  /** Valor controlado do picker. */
  value?: Date | null;
  /**
   * Placeholder do input.
   * @default conforme o `mode` (`Selecione uma data` | `Selecione um horário` | `Selecione data e horário`)
   */
  placeholder?: string;
  /** Desabilita o input e o picker. */
  disabled?: boolean;
  /** Exibe o indicador visual e marca o input como obrigatório. */
  required?: boolean;
  /** Força o estado visual de erro. */
  error?: boolean;
  /** Mensagem de erro exibida abaixo do input. Também ativa o estado de erro. */
  errorMessage?: ReactNode;
  /** Classe CSS do container (`Form.Field`). */
  containerClassName?: string;
  /** Estilo inline do container (`Form.Field`). */
  containerStyle?: CSSProperties;
  /** Classe CSS da mensagem de erro. */
  errorClassName?: string;
  /** Estilo inline da mensagem de erro. */
  errorStyle?: CSSProperties;
  /** Classe CSS do label. */
  labelClassName?: string;
  /** Estilo inline do label. */
  labelStyle?: CSSProperties;
  /** Classe CSS do input customizado. */
  className?: string;
  /**
   * Exibe o ícone de limpar quando há valor selecionado.
   * @default false
   */
  clearable?: boolean;
  /**
   * Tipo de seleção: `date`, `time` ou `datetime`.
   *
   * Quando omitido, é inferido:
   * - `datetime` se `showTimeInput` ou `showTimeSelect` estiver ativo
   * - `date` caso contrário
   *
   * Em `mode="time"`, o ícone passa a ser `faClock`, só a lista de horários é
   * exibida e a digitação é mascarada como `HH:mm`.
   */
  mode?: CalendarInputMode;
  /**
   * Exibe input livre de horário no modo `datetime`.
   * Sem `mode`, ativa o modo `datetime`.
   * @default false
   */
  showTimeInput?: boolean;
  /**
   * Exibe lista de horários no modo `datetime`.
   * Sem `mode`, ativa o modo `datetime`.
   * Em `mode="time"`, a lista é sempre usada.
   * @default false
   */
  showTimeSelect?: boolean;
  /**
   * Label do input de horário (quando `showTimeInput` está ativo).
   * @default 'Horário:'
   */
  timeInputLabel?: string;
  /**
   * Formato da data (`date-fns`).
   * @default 'dd/MM/yyyy'
   */
  dateFormat?: string;
  /**
   * Formato do horário (`date-fns`).
   * A máscara de digitação do `mode="time"` só é aplicada no formato padrão.
   * @default 'HH:mm'
   */
  timeFormat?: string;
  /**
   * Intervalo da lista de horários, em minutos.
   * @default 15
   */
  timeIntervals?: number;
  /** Data mínima selecionável. Ignorado em `mode="time"`. */
  minDate?: Date;
  /** Data máxima selecionável. Ignorado em `mode="time"`. */
  maxDate?: Date;
  /**
   * Marca o picker como início de um range de datas.
   * @default false
   */
  selectsStart?: boolean;
  /**
   * Marca o picker como fim de um range de datas.
   * @default false
   */
  selectsEnd?: boolean;
  /** Data inicial do range (usado com `selectsStart` / `selectsEnd`). */
  startDate?: Date | null;
  /** Data final do range (usado com `selectsStart` / `selectsEnd`). */
  endDate?: Date | null;
  /** Texto do botão de atalho para a data atual. Ignorado em `mode="time"`. */
  todayButton?: string;
  /**
   * Renderiza o picker em portal (overlay). Útil em mobile e dentro de modais.
   * Ignorado em `mode="time"`, pois a lista deve permanecer ancorada ao campo.
   * @default false
   */
  withPortal?: boolean;
  /**
   * Posição do popper do calendário.
   * @default 'bottom-start'
   */
  popperPlacement?:
    | 'auto'
    | 'auto-start'
    | 'auto-end'
    | 'top'
    | 'top-start'
    | 'top-end'
    | 'bottom'
    | 'bottom-start'
    | 'bottom-end'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'left'
    | 'left-start'
    | 'left-end';
  /** Callback disparado ao alterar a data/horário selecionado. */
  onChange?: (date: Date | null) => void;
  /** Callback disparado ao perder o foco. */
  onBlur?: () => void;
  /** Callback disparado ao receber o foco. */
  onFocus?: () => void;
}

const PLACEHOLDERS: Record<CalendarInputMode, string> = {
  date: 'Selecione uma data',
  time: 'Selecione um horário',
  datetime: 'Selecione data e horário',
};

const MODE_ICONS: Record<CalendarInputMode, IconDefinition> = {
  date: faCalendar,
  time: faClock,
  datetime: faCalendar,
};

/** Formato de horário para o qual a máscara de digitação é aplicada. */
const MASKED_TIME_FORMAT = 'HH:mm';
const MASKED_TIME_LENGTH = MASKED_TIME_FORMAT.length;
const MAX_TIME_DIGITS = 4;
const MAX_HOURS = 23;
const MAX_MINUTES = 59;

/** Classe usada para posicionar e empilhar apenas o popup de horário. */
const TIME_POPPER_CLASS = 'calendar-time-popper';
/** Container do portal onde o popup de horário é renderizado. */
const TIME_POPPER_PORTAL_ID = 'calendar-input-time-popper';

/**
 * Posiciona o popup pela viewport, e não pelo container da página, que o corta
 * quando tem scroll. Assim ele fica ancorado ao campo e escolhe abrir para cima
 * ou para baixo conforme o espaço disponível.
 */
const TIME_POPPER_PROPS = { strategy: 'fixed' };
const TIME_POPPER_MODIFIERS = [
  {
    name: 'flip',
    options: {
      fallbackPlacements: ['bottom-start', 'top-start'],
      rootBoundary: 'viewport',
      padding: 8,
    },
  },
  {
    name: 'preventOverflow',
    options: { rootBoundary: 'viewport', padding: 8 },
  },
];

const clampTimePart = (part: string, max: number) =>
  String(Math.min(Number(part), max)).padStart(part.length, '0');

/**
 * Formata a digitação livre no padrão `HH:mm`, limitando horas e minutos.
 */
const maskTimeText = (rawText: string, previousText: string) => {
  const isDeleting = rawText.length < previousText.length;
  const digits = rawText.replace(/\D/g, '').slice(0, MAX_TIME_DIGITS);

  if (!digits) return '';

  if (digits.length === 1) {
    // Dígito único acima de 2 só pode ser hora (ex.: "9" -> "09:")
    return Number(digits) > 2 && !isDeleting ? `0${digits}:` : digits;
  }

  if (digits.length === 2) {
    // Hora inexistente: o primeiro dígito vira a hora (ex.: "25" -> "02:5")
    if (Number(digits) > MAX_HOURS) return `0${digits[0]}:${digits[1]}`;
    return isDeleting ? digits : `${digits}:`;
  }

  return `${clampTimePart(digits.slice(0, 2), MAX_HOURS)}:${clampTimePart(
    digits.slice(2),
    MAX_MINUTES,
  )}`;
};

/**
 * Converte o texto `HH:mm` em `Date`, mantendo a data do valor atual e
 * zerando segundos. Retorna `undefined` enquanto o horário estiver incompleto.
 */
const parseTimeText = (text: string, baseDate: Date) => {
  if (text.length < MASKED_TIME_LENGTH) return undefined;

  const parsed = parse(text, MASKED_TIME_FORMAT, baseDate);
  if (!isValid(parsed)) return undefined;

  return set(parsed, { seconds: 0, milliseconds: 0 });
};

const CustomInput = forwardRef<
  HTMLInputElement,
  {
    value?: string;
    onClick?: () => void;
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: () => void;
    placeholder?: string;
    disabled?: boolean;
    required?: boolean;
    className?: string;
    clearable?: boolean;
    onClear?: () => void;
    onInputBlur?: () => void;
    isMobile?: boolean;
    icon?: IconDefinition;
    /** Quando true, o texto exibido vem apenas de `value` (quem digita é controlado pelo pai). */
    isTextControlled?: boolean;
  }
>(
  (
    {
      value,
      onClick,
      onChange,
      onBlur,
      placeholder,
      disabled,
      required,
      className,
      clearable,
      onClear,
      onInputBlur,
      isMobile,
      icon = faCalendar,
      isTextControlled = false,
    },
    ref,
  ) => {
    const [inputValue, setInputValue] = useState('');
    const displayValue = isTextControlled ? value ?? '' : inputValue;
    const hasValue = displayValue.trim() !== '';

    useEffect(() => {
      if (isTextControlled) return;

      if (value) {
        setInputValue(value);
      }
      if (!value && hasValue) {
        setInputValue('');
      }
    }, [value, isTextControlled]);

    const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (!isTextControlled) {
        setInputValue(event.target.value);
      }

      onChange?.(event);
    };

    const handleBlur = () => {
      if (!isTextControlled && value) {
        setInputValue(value);
      }
      onInputBlur?.();
      onBlur?.();
    };

    return (
      <div
        onClick={onClick}
        className={`${styles.inputWrapper} ${className || ''}`}
      >
        <input
          ref={ref}
          value={displayValue}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={isMobile ? e => e.target.blur() : undefined}
          onClick={isMobile ? onClick : undefined}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          readOnly={isMobile}
          className={`${styles.input} ${isMobile ? styles.mobileInput : ''}`}
        />
        <div className={styles.icons}>
          {clearable && hasValue ? (
            <FontAwesomeIcon
              color={colors.gray10}
              onClick={e => {
                if (disabled) return;
                e.preventDefault();
                e.stopPropagation();
                setInputValue('');
                onClear?.();
              }}
              icon={faTimes}
            />
          ) : (
            <FontAwesomeIcon icon={icon} className={styles.calendarIcon} />
          )}
        </div>
      </div>
    );
  },
);

CustomInput.displayName = 'CustomInput';

const resolveMode = ({
  mode,
  showTimeInput,
  showTimeSelect,
}: {
  mode?: CalendarInputMode;
  showTimeInput: boolean;
  showTimeSelect: boolean;
}): CalendarInputMode => {
  if (mode) return mode;
  if (showTimeInput || showTimeSelect) return 'datetime';
  return 'date';
};

/**
 * Input de data e/ou horário baseado em `react-datepicker`, com locale `pt-BR`.
 *
 * Controla a seleção via `mode`:
 * - `date` — somente data (`faCalendar`)
 * - `time` — somente horário (`faClock`)
 * - `datetime` — data e horário (`faCalendar`)
 *
 * Sem `mode`, o tipo é inferido a partir de `showTimeInput` / `showTimeSelect`.
 *
 * No `mode="time"` a digitação é mascarada como `HH:mm` e o horário só é
 * aplicado quando está completo, preservando a data do valor atual.
 *
 * @example
 * // Somente data
 * <CalendarInput name="birthDate" mode="date" value={date} onChange={setDate} />
 *
 * @example
 * // Somente horário
 * <CalendarInput name="startTime" mode="time" value={time} onChange={setTime} />
 *
 * @example
 * // Data e horário
 * <CalendarInput
 *   name="eventAt"
 *   mode="datetime"
 *   showTimeInput
 *   value={value}
 *   onChange={setValue}
 * />
 *
 * @see {@link CalendarInputProps}
 * @see {@link CalendarInputMode}
 */
export const CalendarInput = ({
  name,
  label,
  value,
  placeholder,
  disabled,
  required,
  error,
  errorMessage,
  containerClassName,
  containerStyle,
  errorClassName,
  errorStyle,
  labelClassName,
  labelStyle,
  className,
  clearable = false,
  mode,
  showTimeInput = false,
  showTimeSelect = false,
  timeInputLabel = 'Horário:',
  dateFormat = 'dd/MM/yyyy',
  timeFormat = 'HH:mm',
  timeIntervals = 15,
  minDate,
  maxDate,
  selectsStart = false,
  selectsEnd = false,
  startDate,
  endDate,
  todayButton,
  withPortal = false,
  popperPlacement = 'bottom-start',
  onChange,
  onBlur,
  onFocus,
}: CalendarInputProps) => {
  const [, setIsOpen] = useState(false);
  const [typedTimeText, setTypedTimeText] = useState<string | null>(null);
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const lastSelectedDateRef = useRef<Date | null>(null);
  const isMobile = useScreenType('mobile');
  const isError = error || !!errorMessage;
  const resolvedMode = resolveMode({ mode, showTimeInput, showTimeSelect });
  const isTimeOnly = resolvedMode === 'time';
  const isDateTime = resolvedMode === 'datetime';
  const shouldShowTimeInput = isDateTime && showTimeInput;
  const shouldShowTimeSelect =
    isTimeOnly || (isDateTime && (showTimeSelect || !showTimeInput));
  const resolvedPlaceholder = placeholder ?? PLACEHOLDERS[resolvedMode];
  const inputIcon = MODE_ICONS[resolvedMode];
  const hasTimeMask = isTimeOnly && timeFormat === MASKED_TIME_FORMAT;

  const getDateFormat = () => {
    if (isTimeOnly) return timeFormat;
    if (isDateTime) return `${dateFormat} ${timeFormat}`;
    return dateFormat;
  };

  const getFormattedValue = () => {
    if (!value) return '';
    try {
      return format(value, getDateFormat());
    } catch {
      return '';
    }
  };

  const displayValue = typedTimeText ?? getFormattedValue();

  const handleChange = (date: Date | null) => {
    setTypedTimeText(null);
    onChange?.(date);

    if (hiddenInputRef.current) {
      let formattedValue = '';
      if (date) {
        try {
          formattedValue = format(date, getDateFormat());
        } catch {
          formattedValue = '';
        }
      }
      hiddenInputRef.current.value = formattedValue;
      const event = new Event('change', { bubbles: true });
      hiddenInputRef.current.dispatchEvent(event);
    }
  };

  const handleClear = () => {
    handleChange(null);
  };

  /**
   * Assume o parse do texto digitado no modo `time`: o react-datepicker aceita
   * formatos parciais e sobrescreve o input a cada tecla, o que atrapalha a
   * digitação. O horário só é aplicado quando `HH:mm` está completo.
   */
  const handleTimeInputChange = (event: FocusEvent<HTMLInputElement>) => {
    // O react-datepicker também dispara onChangeRaw ao selecionar na lista
    if (event.type !== 'change') return;

    event.preventDefault();

    const maskedText = maskTimeText(event.target.value, displayValue);

    if (!maskedText) {
      handleChange(null);
      return;
    }

    // Mantém a data mesmo depois de apagar o campo, já que no modo `time` o
    // usuário não tem como escolhê-la novamente
    const baseDate = value ?? lastSelectedDateRef.current ?? new Date();
    const parsedTime = parseTimeText(maskedText, baseDate);

    if (parsedTime) {
      handleChange(parsedTime);
      return;
    }

    setTypedTimeText(maskedText);
  };

  /** Descarta o texto incompleto, voltando para o horário selecionado. */
  const handleTimeInputBlur = () => {
    setTypedTimeText(null);
  };

  useEffect(() => {
    if (hiddenInputRef.current) {
      hiddenInputRef.current.value = value ? getFormattedValue() : '';
    }
  }, [value]);

  useEffect(() => {
    if (value) {
      lastSelectedDateRef.current = value;
    }
  }, [value]);

  return (
    <Form.Field
      name={name}
      className={`${styles.CalendarInput} ${containerClassName || ''} ${
        disabled ? styles.disabled : ''
      } ${isError ? styles.error : ''}`}
      style={containerStyle}
    >
      {label && (
        <Form.Label
          htmlFor={name}
          className={`${styles.label} ${labelClassName || ''}`}
          style={labelStyle}
        >
          {label}
          {required && (
            <span className={styles.requiredIndicator} aria-hidden="true">
              {' '}
              *
            </span>
          )}
        </Form.Label>
      )}

      <div className={styles.datePickerWrapper}>
        <Form.Control asChild>
          <input
            ref={hiddenInputRef}
            type="hidden"
            name={name}
            value={value ? getFormattedValue() : ''}
            onChange={() => {}}
            aria-required={required}
          />
        </Form.Control>

        <DatePicker
          id={name}
          selected={value}
          onChange={handleChange}
          onCalendarOpen={() => setIsOpen(true)}
          onCalendarClose={() => setIsOpen(false)}
          onBlur={onBlur}
          onFocus={onFocus}
          customInput={
            <CustomInput
              disabled={disabled}
              required={required}
              className={className}
              placeholder={resolvedPlaceholder}
              clearable={clearable}
              onClear={handleClear}
              value={displayValue}
              isMobile={isMobile}
              icon={inputIcon}
              isTextControlled={hasTimeMask}
              onInputBlur={hasTimeMask ? handleTimeInputBlur : undefined}
            />
          }
          onChangeRaw={hasTimeMask ? handleTimeInputChange : undefined}
          // O react-datepicker sobrescreve estas props do customInput
          value={hasTimeMask ? displayValue : undefined}
          placeholderText={resolvedPlaceholder}
          disabled={disabled}
          required={required}
          dateFormat={getDateFormat()}
          showTimeInput={shouldShowTimeInput}
          showTimeSelect={shouldShowTimeSelect}
          showTimeSelectOnly={isTimeOnly}
          timeInputLabel={timeInputLabel}
          timeFormat={timeFormat}
          timeIntervals={timeIntervals}
          minDate={isTimeOnly ? undefined : minDate}
          maxDate={isTimeOnly ? undefined : maxDate}
          selectsStart={!isTimeOnly && selectsStart}
          selectsEnd={!isTimeOnly && selectsEnd}
          startDate={isTimeOnly ? undefined : startDate}
          endDate={isTimeOnly ? undefined : endDate}
          todayButton={isTimeOnly ? undefined : todayButton}
          withPortal={!isTimeOnly && withPortal}
          popperPlacement={popperPlacement}
          popperClassName={isTimeOnly ? TIME_POPPER_CLASS : undefined}
          portalId={isTimeOnly ? TIME_POPPER_PORTAL_ID : undefined}
          popperProps={isTimeOnly ? TIME_POPPER_PROPS : undefined}
          popperModifiers={isTimeOnly ? TIME_POPPER_MODIFIERS : undefined}
          locale="pt-BR"
          className={styles.datePicker}
          calendarClassName={styles.calendar}
          wrapperClassName={styles.wrapper}
        />
      </div>

      {errorMessage && (
        <Form.Message
          className={`${styles.errorMessage} ${errorClassName || ''}`}
          style={errorStyle}
        >
          {errorMessage}
        </Form.Message>
      )}
    </Form.Field>
  );
};
